import React from "react";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  const value = bytes / 1024 ** i;
  return `${value.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

export function formatDate(value?: string | Date | null) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function PanelCard({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-2xl border border-slate-200/80 bg-white shadow-sm shadow-slate-200/40 dark:border-slate-700/80 dark:bg-slate-800/60 dark:shadow-none ${className}`}
    >
      {children}
    </div>
  );
}

export function SectionHeader({
  icon: Icon,
  title,
  subtitle,
}: {
  icon: React.ElementType;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="mb-3 flex items-start gap-2.5">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-primary/20">
        <Icon sx={{ fontSize: 20 }} />
      </div>
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white tracking-tight">
          {title}
        </h3>
        {subtitle ? (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>
        ) : null}
      </div>
    </div>
  );
}

export function DetailRow({ label, value }: { label: string; value?: React.ReactNode }) {
  const display =
    value === undefined || value === null || value === "" ? "—" : value;
  return (
    <div className="group flex flex-col gap-1 rounded-xl px-3 py-2.5 transition-colors hover:bg-slate-50/80 dark:hover:bg-slate-700/30 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <dt className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="text-sm font-semibold text-slate-800 dark:text-slate-100 sm:text-right break-words">
        {display}
      </dd>
    </div>
  );
}

export function DetailsBlock({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/50 divide-y divide-slate-100/80 dark:border-slate-700/60 dark:bg-slate-900/30 dark:divide-slate-700/50">
      {children}
    </div>
  );
}

export function BoolBadge({ value }: { value?: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
        value
          ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60 dark:bg-emerald-950/50 dark:text-emerald-300 dark:ring-emerald-800/50"
          : "bg-slate-100 text-slate-600 ring-1 ring-slate-200/60 dark:bg-slate-700/50 dark:text-slate-300 dark:ring-slate-600/50"
      }`}
    >
      {value ? (
        <>
          <VerifiedOutlinedIcon sx={{ fontSize: 12 }} />
          Yes
        </>
      ) : (
        "No"
      )}
    </span>
  );
}

export function MetricTile({
  label,
  value,
  accent = false,
}: {
  label: string;
  value: React.ReactNode;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-3 sm:p-3.5 ${
        accent
          ? "border-primary/20 bg-gradient-to-br from-primary/[0.07] to-white dark:from-primary/15 dark:to-slate-800/80 dark:border-primary/30"
          : "border-slate-100 bg-white dark:border-slate-700/60 dark:bg-slate-800/40"
      }`}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p
        className={`mt-1.5 text-xl font-bold tracking-tight ${
          accent ? "text-primary" : "text-slate-900 dark:text-white"
        }`}
      >
        {value}
      </p>
    </div>
  );
}
